import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import InvoiceRow from '@/components/InvoiceRow';
import SectionIcon from '@/components/SectionIcon';
import Avatar from '@/components/Avatar';
import EmptyState from '@/components/EmptyState';
import InvoiceDetailModal from '@/components/modals/InvoiceDetailModal';
import { I } from '@/lib/icons';
import { fmtEur, fmtDate, isOverdue, quarterOf } from '@/lib/format';
import { useWorkspace, useClients, useInvoices, useExpenses } from '@/hooks/useData';

export default function Dashboard() {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const { invoices } = useInvoices(workspace?.id);
  const { expenses } = useExpenses(workspace?.id);
  const { clients } = useClients(workspace?.id);
  const [detailInv, setDetailInv] = React.useState(null);

  const year = new Date().getFullYear();
  const ytdInvoices = invoices.filter(i => new Date(i.issue_date).getFullYear() === year);
  const ytdExpenses = expenses.filter(e => new Date(e.date).getFullYear() === year);
  const tržbyYTD = ytdInvoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const výdavkyYTD = ytdExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const nezaplatené = invoices
    .filter(i => i.status !== 'paid' && i.status !== 'cancelled')
    .reduce((s, i) => s + Number(i.total || 0), 0);

  // DPH zostatok pre aktuálny kvartál (rough)
  const q = quarterOf(new Date());
  const qStart = new Date(year, (q - 1) * 3, 1);
  const qEnd = new Date(year, q * 3, 0);
  const inQuarter = (d) => {
    const dt = new Date(d);
    return dt >= qStart && dt <= qEnd;
  };
  const dphOut = ytdInvoices.filter(i => inQuarter(i.issue_date))
    .reduce((s, i) => s + Number(i.dph_amount || 0), 0);
  const dphIn = ytdExpenses.filter(e => inQuarter(e.date))
    .reduce((s, e) => s + Number(e.dph_amount || 0), 0);
  const dphZostatok = dphOut - dphIn;

  const overdueCount = invoices.filter(i => isOverdue(i)).length;
  const recent = invoices.slice(0, 5);
  const pinned = clients.filter(c => c.is_pinned);

  if (invoices.length === 0 && clients.length === 0 && expenses.length === 0) {
    return (
      <>
        <PageHeader title="Prehľad" meta={`Rok ${year}`} accent="green"
          right={<button onClick={() => navigate('/faktury/nova')} className="cd-btn cd-btn-primary" data-testid="new-invoice-btn"><I.plus /> Nová faktúra</button>}
        />
        <NewUserHero onNew={() => navigate('/faktury/nova')} onAddClient={() => navigate('/klienti')} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Prehľad" meta={`Rok ${year}`} accent="green"
        right={<button onClick={() => navigate('/faktury/nova')} className="cd-btn cd-btn-primary" data-testid="new-invoice-btn"><I.plus /> Nová faktúra</button>}
      />

      {/* 4 hero stats */}
      <div style={{ padding: '12px 32px 4px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <BigStat label="Tržby YTD" value={fmtEur(tržbyYTD)} sub={`${ytdInvoices.length} faktúr`} accent="green" highlight />
        <BigStat label="Výdavky YTD" value={fmtEur(výdavkyYTD)} sub={`${ytdExpenses.length} dokladov`} accent="orange" />
        <BigStat label="Nezaplatené FA" value={fmtEur(nezaplatené)} sub={overdueCount > 0 ? `${overdueCount} po splatnosti` : 'všetko v poriadku'} accent={overdueCount > 0 ? 'red' : 'blue'} />
        <BigStat label={`DPH zostatok Q${q}`} value={fmtEur(dphZostatok)} sub={dphZostatok >= 0 ? 'k úhrade' : 'na vrátenie'} accent="yellow" />
      </div>

      {/* Pinned clients */}
      {pinned.length > 0 && (
        <div style={{ padding: '24px 32px 0' }}>
          <SectionTitle>Pripnutí klienti</SectionTitle>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {pinned.map(c => {
              const last = invoices.find(i => i.client_id === c.id);
              return (
                <button key={c.id} onClick={() => navigate('/klienti')} style={{
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-lg)', padding: 14, minWidth: 220,
                  display: 'flex', alignItems: 'center', gap: 12,
                  textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  <Avatar name={c.name} size={40} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>{c.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {last ? `posl. ${fmtDate(last.issue_date)} · ${fmtEur(last.total)}` : 'bez faktúry'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent invoices */}
      <div style={{ padding: '24px 32px 32px' }}>
        <SectionTitle right={
          <button onClick={() => navigate('/faktury')} style={{
            fontSize: 12, color: 'var(--product-primary-hover)', cursor: 'pointer',
            background: 'transparent', border: 'none', fontFamily: 'inherit',
          }}>Zobraziť všetky →</button>
        }>Nedávne faktúry</SectionTitle>
        {recent.length === 0 ? (
          <EmptyState icon="doc" color="blue"
            title="Zatiaľ žiadne faktúry"
            desc="Vystav prvú faktúru za 60 sekúnd. PDF vygeneruje appka, klienta pridáš za pochodu."
            ctaLabel="Nová faktúra" onCta={() => navigate('/faktury/nova')} />
        ) : (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)', overflow: 'hidden',
          }}>
            {recent.map((inv, i) => (
              <InvoiceRow key={inv.id} invoice={inv} divider={i < recent.length - 1}
                onClick={() => setDetailInv(inv)} />
            ))}
          </div>
        )}
      </div>

      <InvoiceDetailModal
        open={!!detailInv}
        invoice={detailInv}
        onClose={() => setDetailInv(null)}
      />
    </>
  );
}

function BigStat({ label, value, sub, accent = 'green', highlight }) {
  return (
    <div style={{
      background: highlight ? `var(--sec-${accent}-soft)` : 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--r-lg)', padding: '14px 16px',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontSize: typeof value === 'string' && value.length > 12 ? 22 : 26,
        fontWeight: 700, letterSpacing: '-0.02em',
        fontFamily: 'var(--font-display)',
        color: highlight ? `var(--sec-${accent})` : 'var(--text-primary)',
      }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
    </div>
  );
}

function SectionTitle({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{children}</h2>
      {right}
    </div>
  );
}

function NewUserHero({ onNew, onAddClient }) {
  return (
    <div style={{ padding: '8px 32px 32px' }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--product-primary-soft), #fff8e7)',
        borderRadius: 'var(--r-lg)', padding: '32px 28px',
        border: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 22,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: 'var(--product-primary-hover)',
            textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8,
          }}>Začni tu</div>
          <h2 style={{
            margin: '0 0 8px', fontSize: 28, fontWeight: 700,
            fontFamily: 'var(--font-display)', letterSpacing: '-0.025em', lineHeight: 1.1,
          }}>Vystav prvú faktúru za 60 sekúnd.</h2>
          <p style={{
            margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)',
            lineHeight: 1.5, maxWidth: 420,
          }}>
            Pridaj klienta, vyplň pár polí, klikni Vystaviť. PDF vygeneruje appka,
            klient v adresári, číslo v rade.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onNew} className="cd-btn cd-btn-primary" data-testid="hero-new-invoice"><I.plus /> Nová faktúra</button>
            <button onClick={onAddClient} className="cd-btn cd-btn-secondary"><I.plus /> Pridať klienta</button>
          </div>
        </div>
      </div>
    </div>
  );
}
